import React, { useEffect, useRef, useState } from 'react';
import type { TodoTask } from '../../types/todo';

type StepListProps = {
  steps: TodoTask['subtasks'];
  onToggleStep: (stepId: number, completed: boolean) => void;
  onRenameStep: (stepId: number, title: string) => void;
  onDeleteStep: (stepId: number) => void;
  onCreateStep: (title: string) => void;
};

type StepRowProps = {
  step: TodoTask['subtasks'][number];
  onToggleStep: (stepId: number, completed: boolean) => void;
  onRenameStep: (stepId: number, title: string) => void;
  onDeleteStep: (stepId: number) => void;
};

const StepRow: React.FC<StepRowProps> = ({ step, onToggleStep, onRenameStep, onDeleteStep }) => {
  const [title, setTitle] = useState(step.title);
  const inputRef = useRef<HTMLInputElement>(null);

  // 服务端值变了（改名成功后的重载、或换了任务）就跟着重置
  useEffect(() => {
    setTitle(step.title);
  }, [step.id, step.title]);

  const commit = () => {
    const next = title.trim();
    // 空标题存不进去（模型 validates :title, presence: true），与其让它报错，
    // 不如直接回滚 —— 用户想删就用旁边的 ✕。
    if (!next) {
      setTitle(step.title);
      return;
    }
    if (next !== step.title) onRenameStep(step.id, next);
  };

  return (
    <div className="group flex items-center gap-[9px] py-[5px] text-[13px] text-todo-ink2">
      <input
        type="checkbox"
        className="todo-check todo-check-sm"
        checked={step.completed}
        onChange={(event) => onToggleStep(step.id, event.target.checked)}
        aria-label={step.completed ? '标为未完成' : '标为完成'}
      />
      <input
        ref={inputRef}
        value={title}
        onChange={(event) => setTitle(event.target.value)}
        onBlur={commit}
        onKeyDown={(event) => {
          if (event.key === 'Enter') inputRef.current?.blur();
          if (event.key === 'Escape') {
            setTitle(step.title);
            inputRef.current?.blur();
          }
        }}
        className={`min-w-0 flex-1 border-0 bg-transparent outline-none ${
          step.completed ? 'text-todo-muted line-through' : 'text-todo-ink2'
        }`}
      />
      <button
        type="button"
        title="删除这个步骤"
        aria-label="删除步骤"
        onClick={() => onDeleteStep(step.id)}
        className="flex-none px-1 text-[12px] text-todo-line2 opacity-0 transition-opacity hover:text-todo-urgent focus:opacity-100 group-hover:opacity-100"
      >
        ✕
      </button>
    </div>
  );
};

// 步骤就是带 parent_id 的子任务，层级固定两层（Task 模型的 parent_must_be_a_root）。
const StepList: React.FC<StepListProps> = ({
  steps,
  onToggleStep,
  onRenameStep,
  onDeleteStep,
  onCreateStep,
}) => {
  const [draft, setDraft] = useState('');

  return (
    <div className="border-t border-todo-line px-[13px] pb-[9px] pt-1">
      {steps.map((step) => (
        <StepRow
          key={step.id}
          step={step}
          onToggleStep={onToggleStep}
          onRenameStep={onRenameStep}
          onDeleteStep={onDeleteStep}
        />
      ))}

      <label className="flex items-center gap-[9px] pb-0.5 pt-1.5 text-[13px] text-todo-muted">
        <span className="text-[14px]">＋</span>
        <input
          value={draft}
          onChange={(event) => setDraft(event.target.value)}
          onKeyDown={(event) => {
            if (event.key !== 'Enter') return;
            const value = draft.trim();
            if (!value) return;
            onCreateStep(value);
            setDraft('');
          }}
          placeholder="添加步骤"
          className="flex-1 border-0 bg-transparent text-[13px] text-todo-ink outline-none placeholder:text-todo-muted"
        />
      </label>
    </div>
  );
};

export default StepList;
